import { useAuth } from "@/auth/AuthContext";
import { SaveBar } from "@/components/portal/SaveBar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GeneralSettingsSection } from "@/components/settings/GeneralSettingsSection";
import { PayablesSettingsSection } from "@/components/settings/PayablesSettingsSection";
import { SpendCategoriesSection } from "@/components/settings/SpendCategoriesSection";
import { ReceivablesSettingsSection } from "@/components/settings/ReceivablesSettingsSection";
import { InvoicePdfSettingsSection } from "@/components/settings/InvoicePdfSettingsSection";
import { ExpensesSettingsSection } from "@/components/settings/ExpensesSettingsSection";
import { WhatsAppSettingsSection, useWhatsAppConnection } from "@/components/settings/WhatsAppSettingsSection";

// Kept mounted when hidden, so edits on one tab survive switching to another and the one save bar saves them all.
const TAB = "flex flex-col gap-6 data-[state=inactive]:hidden";

/** Client settings tabs, each backed by its own endpoint (/client, /settings/ap, /settings/ar, /settings/expenses, /whatsapp/connection). */
export function SettingsPage() {
  const { activeClient, activeClientId } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";
  const whatsApp = useWhatsAppConnection();
  // Hidden until the read settles, so a Client without the module never sees the tab flash in.
  const showWhatsApp = !whatsApp.loading && !whatsApp.notEntitled;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-8">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Client-level configuration.</p>
      </div>

      <Tabs defaultValue="general" className="flex flex-col gap-6">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="payables">Payables</TabsTrigger>
          <TabsTrigger value="receivables">Receivables</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          {showWhatsApp && <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>}
        </TabsList>

        <TabsContent value="general" forceMount className={TAB}>
          <GeneralSettingsSection />
        </TabsContent>

        <TabsContent value="payables" forceMount className={TAB}>
          <PayablesSettingsSection />
          {activeClientId && <SpendCategoriesSection clientId={activeClientId} isAdmin={isAdmin} />}
        </TabsContent>

        <TabsContent value="receivables" forceMount className={TAB}>
          <ReceivablesSettingsSection />
          <InvoicePdfSettingsSection />
        </TabsContent>

        <TabsContent value="expenses" forceMount className={TAB}>
          <ExpensesSettingsSection />
        </TabsContent>

        {showWhatsApp && (
          <TabsContent value="whatsapp" forceMount className={TAB}>
            <WhatsAppSettingsSection query={whatsApp} />
          </TabsContent>
        )}
      </Tabs>

      <SaveBar />
    </div>
  );
}
